import { InteractionRequiredAuthError, BrowserAuthError, type AuthenticationResult } from "@azure/msal-browser";
import { askAmiApiTokenRequest, msalInstance } from "Auth/authConfig";
import { getCookie, removeCookie, setCookie } from "Common/Functions";

export const getAccessToken = async (): Promise<string | null> => {
  try {
    const activeAccount = msalInstance.getActiveAccount();
    if (!activeAccount && isUserLoggedOut()) {
      await handleUserLogout();
      return null;
    } else {
      const currentUserId = getCookie("currentuserid");
      const accessTokenResponse = await msalInstance.acquireTokenSilent(askAmiApiTokenRequest);

      if (await isAccountChanged(accessTokenResponse, currentUserId)) {
        await handleAccountChange();
        return null;
      }

      if (currentUserId === null && accessTokenResponse.account) {
        setCookie("currentuserid", accessTokenResponse.account.localAccountId, 1);
      }

      return accessTokenResponse.accessToken;
    }
  } catch (error) {
    console.log("Error acquiring access token:", error);
    return await handleAuthErrors(error);
  }
};

const isUserLoggedOut = (): boolean => {
  const activeAccount = msalInstance.getActiveAccount();
  const isLoggedOutFlag = localStorage.getItem("isUserLoggedOut") === "true";
  return isLoggedOutFlag && !activeAccount;
};

const handleUserLogout = async (): Promise<void> => {
  await msalInstance.logoutRedirect({
    postLogoutRedirectUri: "/",
  });
};

const isAccountChanged = async (
  accessTokenResponse: AuthenticationResult,
  currentUserId: string | null,
): Promise<boolean> => {
  return currentUserId !== null && accessTokenResponse.account.localAccountId !== currentUserId;
};

const handleAccountChange = async (): Promise<void> => {
  localStorage.setItem("isUserLoggedOut", "true");
  removeCookie("currentuserid");
  await msalInstance.logoutRedirect({
    postLogoutRedirectUri: "/",
  });
};

let isRedirectingToLogin = false;

const triggerLoginRedirect = async (useAcquireToken: boolean): Promise<boolean> => {
  if (isRedirectingToLogin) return false;
  isRedirectingToLogin = true;
  try {
    if (useAcquireToken) {
      await msalInstance.acquireTokenRedirect(askAmiApiTokenRequest);
    } else {
      await msalInstance.loginRedirect();
    }
    return true;
  } catch (err) {
    isRedirectingToLogin = false;
    console.error("Login redirect error:", err);
    return false;
  }
};

const handleAuthErrors = async (error: unknown): Promise<string | null> => {
  if (error instanceof InteractionRequiredAuthError) {
    // Token expired and B2C session expired — need interactive login
    await triggerLoginRedirect(true);
    return null;
  } else if (error instanceof BrowserAuthError) {
    if (error.errorCode === "interaction_in_progress") {
      // A redirect is already in progress — do nothing
      return null;
    }
    // timed_out: MSAL v5 silent iframe attempt timed out (redirect bridge not configured)
    // treat same as InteractionRequired — need interactive login
    await triggerLoginRedirect(true);
    return null;
  } else {
    console.error("Unexpected auth error:", error);
    await triggerLoginRedirect(false);
    return null;
  }
};
